/* eslint-disable react-hooks/rules-of-hooks */
import { Fragment, useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "@reach/router";
import { useTranslation } from "react-i18next";
import { Typeahead, Hint } from "react-bootstrap-typeahead";
import set from "lodash/set";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import cx from "classnames";
import { Tooltip } from "react-tooltip";

import useFilters from "../../../hooks/useFilters";
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import getModal from "../../../lib/getModal";
import getAccessToken from "../../../hooks/getAccessToken";
import BatchSessionsModal from './BatchSessionsModal'
import SendToPatient from '../SendToPatient/SendToPatient'
import GroupEventsModal from './GroupEventsModal'
import CancelGroupEventModal from './CancelGroupEventModal'
import SessionsToast from '../../Notify/SessionsToast'
import Text from "../../Form/Text";
import useStore from "../useStore";
import useStoreRooms from "App/Room/useStore";
import Select from "../../Form/Select";
import { formatDate } from "../../../config/i18n";
import _onChangeConsumer from "../../Consumers/onChangeConsumer";
import styles from "./styles.module.scss";
import Locations from '../Filters/Locations'
import Resources from '../Filters/Resources'
import Services from '../Filters/Services'
import Financiers from '../Filters/Financiers'
import validateConsumer from "../../Consumers/validateConsumer"
import useUser from "hooks/useUser"
import { usePlanLock, LockedLabel } from "Components/FeatureBloqueada/PlanLock";

const GROUP_WATCHED_FIELDS = [
  { key: 'consumer', label: 'paciente', id: e => e?.consumer?.id },
  { key: 'resource', label: 'profesional', id: e => e?.resource?.id },
  { key: 'financier', label: 'financiador', id: e => e?.financier?.key },
  { key: 'location', label: 'ubicación', id: e => e?.location?.id },
]

const { REACT_APP_API_URL } = process.env;

const TOAST_DURATION = 5000

const notifySession = props =>
  window.notify({ duration: TOAST_DURATION, render: ({ onClose }) => <SessionsToast {...props} onClose={onClose} /> })

const UI_FREQUENCY_OPTIONS = [
  { value: 'weekly',   label: 'Semanal' },
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'monthly',  label: 'Mensual' },
]

const fmtShortDate = (d) => {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(2)
  return `${dd}/${mm}/${yy}`
}

const calcEndDate = (startDay, count, frequency) => {
  if (!startDay || count <= 0 || !frequency) return null
  const d = new Date(`${startDay}T12:00`)
  const n = count - 1
  if (frequency === 'weekly') d.setDate(d.getDate() + n * 7)
  else if (frequency === 'biweekly') d.setDate(d.getDate() + n * 14)
  else if (frequency === 'monthly') d.setMonth(d.getMonth() + n)
  else if (frequency === 'yearly') d.setFullYear(d.getFullYear() + n)
  return fmtShortDate(d)
}

const ToggleSwitch = ({ checked, name, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={cx(styles.toggle, { [styles.toggleOn]: checked })}
    onClick={() => onChange({ target: { name, type: 'checkbox' } })}
  >
    <span className={styles.toggleThumb} />
  </button>
)

const renderInput = ({ hint, isInvalid } = {}) => ({
  inputRef,
  referenceElementRef,
  ...inputProps
}) => (
  <Hint
    shouldSelect={(shouldSelect, e) =>
      ["Enter", "Tab"].includes(e.key) || shouldSelect
    }
  >
    <div className="flex-fill w-100">
      <input
        {...inputProps}
        type="text"
        autoComplete="--disabled"
        autoComplete="none"
        autoComplete="off"
        className={cx("form-control", { "is-invalid": isInvalid })}
        ref={(input) => {
          inputRef(input);
          referenceElementRef(input);
        }}
      />
    </div>
  </Hint>
);

const Form = (props) => {
  const now = new Date();
  const location = useLocation();
  const navigate = useNavigate();
  const store = useStore(state => ({
    location: state.location,
    resource: state.resource,
    service: state.service,
    financier: state.financier,
    event: state.event,
    slot: state.slot,
    update: state.update,
  }));
  const [full, setFull] = useState(true);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [consumer, setConsumer] = useState(props.consumer || {});

  // Turnos recurrentes
  const [batchMode, setBatchMode] = useState('single'); // 'single' | 'sessions'
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [recurrence, setRecurrence] = useState({ count: 0, frequency: '' });
  const [batchSlots, setBatchSlots] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [groupCancelLoading, setGroupCancelLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showCancelGroupModal, setShowCancelGroupModal] = useState(false);
  const originalEventRef = useRef(null);
  const isNewEventRef = useRef(false);
  // Identidad estable por instancia de Form — ver el efecto que registra
  // window.callbacks.consumer más abajo.
  const consumerCallbackTokenRef = useRef({});
  const queryClient = useQueryClient();
  const accessToken = getAccessToken();
  const openRoom = useStoreRooms((state) => state.open);
  const user = useUser();
  // Pedido de reunión 2026-07-29 (Juan Cruz): abrir la videoconsulta también
  // navega la página a la HCE del paciente — mismo permiso que ya gatea
  // "agregar indicación"/"adjuntar archivo" en la llamada (Room.js).
  const hasEhr = user.actions.has("ehr");

  const apiFetch = async (path, method, body) => {
    const teamPath = window._l(path).substr(1)
    const res = await fetch(`${REACT_APP_API_URL}/${teamPath}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken.value}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    })
    return res.json().catch(() => null)
  }

  const handleBatchCheck = async () => {
    if (batchLoading) return
    setBatchLoading(true)
    try {
      const result = await apiFetch('/events/batch/check', 'POST', {
        ...event,
        recurrence,
      })
      if (result?.slots) {
        setBatchSlots(result.slots)
        setShowBatchModal(true)
      }
    } catch (e) {
      notifySession({ variant: 'error', title: e.message })
    } finally {
      setBatchLoading(false)
    }
  }

  const fetchAvailability = async (day) => {
    return apiFetch('/availability', 'POST', {
      team: event.team || event.teamID,
      resource: event.resource?.id,
      location: event.location?.id,
      startsAt: day,
      free: true,
    })
  }

  const handleSlotChange = (index, newSlot) => {
    setBatchSlots(prev => prev.map((s, i) => i === index ? newSlot : s))
  }

  const handleBatchCreate = async () => {
    if (batchLoading) return
    setBatchLoading(true)
    try {
      const result = await apiFetch('/events/batch', 'POST', {
        ...event,
        recurrence,
        slots: batchSlots,
      })
      if (result?.groupID) {
        setShowBatchModal(false)
        const createdCount = result.created.length
        const skippedCount = result.skipped?.length || 0
        const total = createdCount + skippedCount
        const createdDays = result.created.map(e => e.day).sort()
        const dateRange = createdDays.length > 1
          ? `${fmtShortDate(new Date(`${createdDays[0]}T12:00`))} → ${fmtShortDate(new Date(`${createdDays[createdDays.length - 1]}T12:00`))}`
          : null
        const freqLabelForToast = UI_FREQUENCY_OPTIONS.find(o => o.value === recurrence.frequency)?.label
        const isPartial = createdCount < total
        notifySession({
          variant: isPartial ? 'warning' : 'success',
          title: isPartial
            ? t('n_of_total_sessions_created', '{{created}} de {{total}} sesiones creadas', { created: createdCount, total })
            : t('n_sessions_created', '{{created}} sesiones creadas correctamente', { created: createdCount }),
          subtitle: !isPartial && freqLabelForToast && dateRange ? `${freqLabelForToast} · ${dateRange}` : undefined,
        })
        setTimeout(() => {
          if (props.onChange) props.onChange(result.created[0])
          onClose()
        }, 0)
      }
    } catch (e) {
      notifySession({ variant: 'error', title: e.message })
    } finally {
      setBatchLoading(false)
    }
  }

  const handleCancelGroup = async (onlyThis = false) => {
    if (!event.groupID) return
    const fromDate = event.day ? fmtShortDate(new Date(`${event.day}T12:00`)) : ''
    if (!window.confirm(onlyThis
      ? '¿Cancelar solo este turno?'
      : `¿Cancelar todas las sesiones desde el ${fromDate} en adelante?`
    )) return
    if (onlyThis) return toggle()
    setGroupCancelLoading(true)
    try {
      const result = await apiFetch(`/events/group/${event.groupID}?from=${event.id}`, 'DELETE')
      notifySession({
        variant: 'cancelled',
        title: result.cancelled === 1
          ? t('one_session_cancelled', '1 sesión cancelada')
          : t('n_sessions_cancelled', '{{n}} sesiones canceladas', { n: result.cancelled }),
      })
      groupEventsQuery.refetch()
      if (props.onChange) props.onChange({ ...event, status: 'cancelled' })
      onClose()
    } catch (e) {
      notifySession({ variant: 'error', title: e.message })
    } finally {
      setGroupCancelLoading(false)
    }
  }

  const handleCancelSingleEvent = async (e) => {
    if (!window.confirm('¿Cancelar este turno?')) return
    if (e.id === event.id) {
      mutate({ ...event, status: 'cancelled' })
      setShowGroupModal(false)
      notifySession({ variant: 'cancelled', title: t('one_session_cancelled', '1 sesión cancelada') })
      return
    }
    try {
      await apiFetch(`/${e.id}`, 'PATCH', { status: 'cancelled' })
      groupEventsQuery.refetch()
      notifySession({ variant: 'cancelled', title: t('one_session_cancelled', '1 sesión cancelada') })
    } catch (err) {
      notifySession({ variant: 'error', title: err.message })
    }
  }

  const invalidateEventsCache = () => {
    queryClient.invalidateQueries({
      // Bug real (auditoría 2026-08-14): el predicate matcheaba por
      // substring 'events' — como el id de ESTE MISMO evento abierto en el
      // form empieza con "events/" (ver el regex /^events\// de más abajo),
      // reprogramar/cancelar OTRA sesión del grupo (que sí necesita
      // invalidar la lista) también invalidaba la query puntual de este
      // evento. El refetch que eso dispara pisaba, sin ningún guard, las
      // ediciones que el usuario tenía sin guardar en el form abierto (ver
      // el efecto de sync de `query.data` más abajo). Se excluye acá la
      // query propia — sigue invalidando la lista/grupo, no el form activo.
      predicate: q => q.queryHash.includes('events') && q.queryKey[0] !== event.id,
    })
  }

  const handleRescheduleFromModal = async (e, { day, time }) => {
    setShowGroupModal(false)
    if (e.id === event.id) {
      mutate({ ...event, day, time })
      invalidateEventsCache()
      notifySession({ variant: 'success', title: t('session_rescheduled', 'Turno reprogramado') })
      return
    }
    try {
      await apiFetch(`/${e.id}`, 'PATCH', { day, time })
      groupEventsQuery.refetch()
      invalidateEventsCache()
      notifySession({ variant: 'success', title: t('session_rescheduled', 'Turno reprogramado') })
    } catch (err) {
      notifySession({ variant: 'error', title: err.message })
    }
  }

  const [dirty, setDirty] = useState(false);
  const { t, i18n } = useTranslation();
  const [warnings, setWarnings] = useState({});
  const [loading, setLoading] = useState(false);

  const [event, setEvent] = useState({
    consumer: {},
    location: {},
    resource: {},
    service: {},
    financier: {},
    day: props.slot?.day || now.toLocaleDateString("fr-ca"),
    time:
      props.slot?.time ||
      now.toLocaleTimeString("it-IT", { timeStyle: "short" }),
    comments: "",
    instructions: "",
    notify: true,
    compute: true,
    overbooking: false,
    remote: false,
    status: "booked",
    type: props.type
  });

  const query = useQuery(event.id, {
    enabled: !!event.id,
  });

  const consumerQuery = useQuery(window._l(`/${event?.consumer?.id}`).substr(1), {
    enabled: !!event.id && !!event?.consumer?.id && !consumer?.id
  });

  const groupEventsQuery = useQuery(
    event.groupID ? window._l(`/events/group/${event.groupID}`).substr(1) : null,
    { enabled: !!event.groupID }
  );

  const [mutate, mutation] = useMutation(
    window._l(`/${event.id || "events"}`).substr(1),
    {
      onMutate: (data) => {
        setEvent(data);
      },
      onSuccess: (data) => {
        if (isNewEventRef.current && data.id) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'turno_creado',
            team_id: data.teamID,
            appointment_type: data.remote ? 'remote' : 'on_site',
            professional_id: data.resource?.id || data.resourceId,
            has_patient: !!data.consumer?.id || !!data.consumerId,
            has_coverage: !!data.financier?.id || !!data.financierId,
          });
        }
        setEvent(data);
        if (props.onChange) props.onChange(data);
      },
      onError: (error) => {
        window.notify({
          message: error.message,
          type: "danger",
          title: "Error",
        });
      },
    }
  );

  const onClose = () => {
    if (props.onClose) props.onClose();
  };

  const consumers = useFilters(
    {
      key: "consumers",
      sort: { key: "label" },
    },
    "skipLocation"
  );

  let date = new Date()
  if (event?.day && event?.time) {
    date = new Date(`${event.day}T${event.time}`)
  } else if (event?.day) {
    date = new Date(event.day)
  } else if (props.slot?.day && props.slot?.time) {
    date = new Date(`${props.slot.day}T${props.slot.time}`)
  } else if (props.slot?.day) {
    date = new Date(props.slot.day)
  }

  const startsAt = date.getTime()
  const filters = {
    free: true,
    location: event?.location?.id,
    resource: event?.resource?.id,
    service: event?.service?.key,
    // financier: event.financier?.key,
    startsAt,
    endsAt: startsAt + 3 * 24 * 60 * 60 * 1000,
    day: event.day,
    time: event.time
  };

  if (props.day === false) {
    delete filters.day
  }
  if (props.time === false) {
    delete filters.time
  }

  const availability = useQuery(window._l(`/availability`).substr(1), {
    body: filters,
    initialData: {},
    enabled: event.day
  });

  const onChangeConsumer = (array) => {
    if (!array || !array[0]) {
      setConsumer({});
      return setEvent({
        ...event,
        consumer: {},
      });
    }

    const consumer = _onChangeConsumer(array);
    const newEvent = JSON.clone(event);

    const financierKeys = Object.keys(availability.data.facets?.financiers || {});
    const financier =
      consumer.financiers.sort((a, b) => {
        const matchA = financierKeys.find((f) => a.id.includes(f));
        const matchB = financierKeys.find((f) => b.id.includes(f));
        const includesA = financierKeys.includes(a.id);
        const includesB = financierKeys.includes(b.id);
        if (includesA && !includesB) return -1;
        if (!includesA && includesB) return 1;
        if (matchA && !matchB) return -1;
        if (!matchA && matchB) return 1;
        return 0;
      })[0] || {};

    newEvent.financier = {
      key: financier.id,
      identification: financier.identification,
      label: financier.label,
    };

    newEvent.consumer = {
      id: consumer.id,
      label: consumer.label,
    };
    setConsumer(consumer);
    setEvent(newEvent);
  };

  const onChange = ({ target: { name, value, type } }) => {
    const newEvent = JSON.clone(event);
    
    if ((name === "day" || name === "untilDay") && value) {
      const yearPart = value.split('-')[0];
      if (yearPart.length > 4) {
        const correctedYear = yearPart.slice(-4);
        const restOfDate = value.split('-').slice(1).join('-');
        value = correctedYear + '-' + restOfDate;
      }
    }
    
    if (name === "service") {
      newEvent.remote = value?.key
        ? /telemedic|videoconsul/g.test(value?.key)
        : newEvent.remote;
      // Sin telemedicina en el plan no se auto-prende en un turno que no la tenia.
      if (telemedicineLock.blocked && !event.remote) newEvent.remote = false;
    }
    if (name === "financier") {
      value.key = value.key || value.id;
    }
    if (value && type === "number") {
      set(newEvent, name, +value);
    } else if (type === "checkbox") {
      if (name === "remote" && event.remote === undefined) {
        set(newEvent, name, false);
      } else if (name === "overbooking" && !get(newEvent, name) && newEvent.compute === true) {
        set(newEvent, "compute", false);
        set(newEvent, "overbooking", true);
      } else {
        set(newEvent, name, !get(newEvent, name));
      }
    } else {
      set(newEvent, name, value);
    }
    setEvent(newEvent);
  };

  const toggle = () => {
    if (mutation.isLoading) return;
    if (event.status !== 'cancelled' && event.groupID) {
      setShowCancelGroupModal(true)
      return
    }
    const status = event.status === "cancelled" ? "booked" : "cancelled";
    if (status === 'cancelled' && !window.confirm(t('event_cancel_confirmation', '¿Confirmar cancelacion?'))) return
    mutate({ ...event, status });
  };

  const handleCancelThisFromGroupModal = () => {
    setShowCancelGroupModal(false)
    mutate({ ...event, status: 'cancelled' })
    invalidateEventsCache()
  }

  const handleCancelFutureFromGroupModal = async () => {
    if (!event.groupID) return
    setGroupCancelLoading(true)
    try {
      const result = await apiFetch(`/events/group/${event.groupID}?from=${event.id}`, 'DELETE')
      setShowCancelGroupModal(false)
      notifySession({
        variant: 'cancelled',
        title: result.cancelled === 1
          ? t('one_session_cancelled', '1 sesión cancelada')
          : t('n_sessions_cancelled', '{{n}} sesiones canceladas', { n: result.cancelled }),
      })
      groupEventsQuery.refetch()
      invalidateEventsCache()
      if (props.onChange) props.onChange({ ...event, status: 'cancelled' })
      onClose()
    } catch (e) {
      notifySession({ variant: 'error', title: e.message })
    } finally {
      setGroupCancelLoading(false)
    }
  }

  const handleViewSessionFromCancelModal = () => {
    setShowCancelGroupModal(false)
    setShowGroupModal(true)
  }

  const save = async () => {
    if (mutation.isLoading) return;

    // Para eventos de grupo, detectar si cambió algún campo crítico
    if (event.groupID && event.id && originalEventRef.current) {
      const orig = originalEventRef.current
      const changedFields = GROUP_WATCHED_FIELDS.filter(
        f => f.id(event) !== f.id(orig)
      )

      if (changedFields.length > 0) {
        const labels = changedFields.map(f => f.label).join(', ')
        const applyToGroup = window.confirm(
          `Cambiaste: ${labels}.\n¿Aplicar este cambio a todos los turnos futuros del grupo?`
        )

        mutate(event)

        if (applyToGroup) {
          const fields = Object.fromEntries(
            changedFields.map(f => [f.key, event[f.key]])
          )
          await apiFetch(`/events/group/${event.groupID}/fields`, 'PATCH', {
            from: event.id,
            fields,
          }).then(() => groupEventsQuery.refetch())
            .catch(err => window.notify({ message: err.message, type: 'danger' }))
        }
        return
      }
    }

    isNewEventRef.current = !event.id;
    mutate(event);
  };

  const reschedule = ({ day, time }) => {
    mutate({ ...event, day, time, status: "booked" })
    store.update({ reschedule: undefined, event: undefined })
  }

  const fetchWarnings = async (consumer) => {
    setLoading(true);
    let warnings = await validateConsumer(consumer);
    warnings = {financiers: warnings.financiers, flag: warnings.financiers?.flag};
    setWarnings(warnings);
    setLoading(false);
  }

  useEffect(() => {
    if (event.type === "lock" && (!event.untilTime || !event.untilDay)) {
      setEvent({
        ...event,
        untilTime: props.isSlot && props.slot?.time ? props.slot?.time : (event.untilTime || "23:59"),
        untilDay: event.day,
      });
    } else if (
      event.type !== "lock" &&
      (!!event.untilTime || !!event.untilDay)
    ) {
      setEvent({
        ...event,
        untilTime: undefined,
        untilDay: undefined,
      });
    }
  }, [event]);

  useEffect(() => {
    if (props.event) {
      setEvent({ ...event, ...props.event });
    }
  }, [props.event]);

  useEffect(() => {
    // Bug real (auditoría 2026-08-14): #consumersModal es un modal ÚNICO y
    // global (ver Consumers/Modal/Modal.js) — con dos Form montados a la
    // vez (ej. un Form de fondo + otro en un modal anidado), cada
    // re-render de CUALQUIERA de los dos reescribe este callback global
    // (necesario para que apunte siempre al `event`/`onChangeConsumer` más
    // reciente de SU instancia). El problema real no es esa reescritura en
    // sí — es el cleanup incondicional: al desmontarse la instancia A
    // borraba el callback SIN chequear si para ese momento la instancia B
    // ya lo había tomado, dejando a B (que sigue montada y activa) sin
    // callback para cuando el usuario elija un paciente.
    const token = consumerCallbackTokenRef.current;
    const callback = (consumer) => {
      getModal("consumersModal", false).hide();
      if (!consumer) return;
      onChangeConsumer([consumer].filter((p) => p.id));
    };
    callback.token = token;
    window.callbacks.consumer = callback;
    return () => {
      if (window.callbacks.consumer?.token === token) {
        delete window.callbacks.consumer;
      }
    };
  });

  useEffect(() => {
    if (props.onClose) {
      window.dispatchEvent(new Event("event.open"));
      window.addEventListener("event.open", props.onClose);
      return () => window.removeEventListener("event.open", props.onClose);
    }
  }, [props.onClose]);

  useEffect(() => {
    originalEventRef.current = null;
  }, [event.id]);

  useEffect(() => {
    const event = JSON.clone(query.data)
    if (query.data?.id && !/^events\/lock-/.test(query.data.id)) {
      setEvent(event);
      originalEventRef.current = event;
    } else if (query.data?.id) {
      event.resource.id ||= store.resource?.id
      event.resource.label ||= store.resource?.label
      setEvent(event);
      originalEventRef.current = event;
    }
  }, [query.data, store.resource]);

  useEffect(() => {
    const updatedAt = Date.now();
    const payload = JSON.clone(event);
    let eventID =
      props.eventID || new URLSearchParams(location.search).get("eventID");
    eventID = /^events\//.test(eventID) ? eventID : "";
    if (eventID && eventID !== event.id) {
      setEvent({ ...event, id: eventID });
    } else if (!dirty && !event.id) {
      if (!event.location.id && store.location?.id) {
        payload.location = store.location;
        payload.updatedAt = updatedAt;
      }

      if (!event.resource.id && store.resource?.id) {
        payload.resource = store.resource;
        payload.updatedAt = updatedAt;
      }

      if (!event.service.key && store.service?.key) {
        payload.service = store.service;
        payload.updatedAt = updatedAt;
      }

      if (
        !event.financier.key &&
        store.financier?.key &&
        !/\/$/.test(store.financier.key)
      ) {
        payload.financier = store.financier;
        payload.updatedAt = updatedAt;
      }

      payload.type = props.type || "appointment";

      setDirty(true);

      if (event.updatedAt !== payload.updatedAt) {
        setEvent(payload);
      }
    }
  }, [
    event,
    store.event,
    store.slot,
    store.location,
    store.resource,
    store.service,
    store.financier,
    location.search,
    props.eventID,
    props.type,
    dirty,
  ]);

  useEffect(()=>{
    if(consumer?.id){
      fetchWarnings(consumer);
    } else {
      setWarnings({})
    }
  }, [consumer]);

  useEffect(()=>{
    if(consumerQuery.data?.id && !consumer.id){

      setConsumer(consumerQuery.data)
    }
  }, [consumerQuery.data])

  // Sesiones y telemedicina por plan. Sesiones: el back corta /events/batch con
  // events.sessions. Telemedicina: solo se intercepta PRENDERLA; un turno que ya es
  // remoto se sigue viendo y se puede apagar (lo que ya lo tiene, sigue).
  // Van ANTES del return del spinner: son hooks, y un early return antes que ellos
  // cambia la cantidad de hooks entre renders (React #300/#310 al abrir un turno).
  const sessionsLock = usePlanLock("events.sessions");
  const telemedicineLock = usePlanLock("events.telemedicine");
  const telemedicineOnBlocked = telemedicineLock.blocked && !event?.remote;

  if (query.isLoading) return (
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{t("lodaing")}</span>
      </div>
    </div>
  );

  const isValid = event && event.time && event.day && event.resource?.id && (
    (event.type !== "lock" && event.consumer?.id && event.location?.id && event.service?.key) ||
    (event.type === "lock" && event.untilDay && event?.untilTime));

  availability.financiers = { facets: {}, consumer: { options: [], label: t('consumer_financiers', 'Coberturas del paciente') } };

  {
    const allFinanciers = i18n?.store?.data?.[i18n?.language]?.pms_financiers || {};
    const allFinanciersKeys = Object.keys(allFinanciers).map(key => `pms_financiers:${key}`);
    for (const [key1, financier] of Object.entries(availability.data.facets?.financiers || {})) {
      if (/\/$/.test(key1)) {
        const keys = allFinanciersKeys.filter(key2 => key2.includes(key1) && key2 !== key1);
        for (const key2 of keys) {
          const candidate = allFinanciers[key2.replace("pms_financiers:", "")];
          availability.financiers.facets[key2] = { ...financier, label: t(key2, candidate) };
        }
      } else {
        availability.financiers.facets[key1] = {
          ...financier,
          label: t(key1, financier.label),
        }
      }
    }
  }

  if (Array.isArray(consumer.financiers)) {
    for (const financier of consumer.financiers) {
      availability.financiers.consumer.options.push({ key: financier.id, label: t(financier.id, financier.label), capacity: 1 })
    }
  }

  const slots = availability.data.slots || {}
  const facets = availability.data.facets || {}

  const freqLabel = UI_FREQUENCY_OPTIONS.find(o => o.value === event.recurrence?.frequency)?.label
  const groupDays = (groupEventsQuery.data ?? []).map(e => e.day).sort()
  const groupStartDate = groupDays[0] ? fmtShortDate(new Date(`${groupDays[0]}T12:00`)) : null
  const groupEndDate = groupDays.length > 1 ? fmtShortDate(new Date(`${groupDays[groupDays.length - 1]}T12:00`)) : null

  const switchBatchMode = (mode) => {
    setBatchMode(mode)
    setBatchSlots([])
    setRecurrence({ count: 2, frequency: '' })
  }

  return (
    <div className={styles.container}>
      {event.status === 'cancelled' && (
        <div className="text-right mt-n2 mb-2">
          <small className="badge bg-danger">{event.type !== 'lock' ? t('event_cancelled_message', "Turno cancelado") : t('lock_cancelled_message', "Bloqueo anulado")}</small>
        </div>
      )}

      {!event.id && event.type !== "lock" && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="m-0">{t('event_form_title', 'Turno')}</h5>
          <button className={cx('btn btn-sm btn-outline-danger')} onClick={() => {
            onChange({ target: { name: "type", value: "lock" } })
          }}>
            {t('lock_time', "Bloquear horario")}
          </button>
        </div>
      )}

      <div className="mb-2">
        {consumer && consumer.blocked && (
          <div className="bg-warning p-2 rounded mb-2">
            {t('consumer_blocked_by_team', 'El paciente esta bloqueado por el equipo. Esto significa que actualmente solo los miembros del equipo pueden agendar turnos con el mismo.')}
          </div>
        )}
        {event.type !== "lock" && (
          <>
            {warnings.financiers?.flag && (
              <div className="bg-warning p-2 rounded mb-2">
                {t('event_warnings_financier', 'El paciente tiene datos incorrectos en la cobertura')}
              </div>
            )}
            <div className="mb-2 position-relative">
              <Typeahead
                selected={[event.consumer].filter((p) => p.id && p.label)}
                isLoading={!!consumers.isLoading}
                isInvalid={!event.consumer?.id || warnings.flag}
                filterBy={() => true}
                inputProps={{
                  id: "consumer",
                }}
                id="consumer"
                allowNew={(results) => !consumers.isLoading}
                onChange={onChangeConsumer}
                onInputChange={(value) => consumers.onChange("q", value)}
                options={consumers.data.flatMap((data) => data)}
                placeholder={t(`consumer_placeholder`, {
                  context: "events",
                })}
                newSelectionPrefix={`${t("new_consumer", {
                  context: "quick_form",
                })}: `}
                emptyLabel={
                  consumers.isLoading ? t(`searching`) : t(`no_matches`)
                }
                paginationText={t(`display_more_results`)}
                promptText={t(`type_to_search`)}
                searchText={t(`searching`)}
                renderMenuItemChildren={(option, props, index) => {
                  const items = [
                    option.identification,
                    ...option.emails.map((e) => e.value),
                  ];
                  return (
                    <div className="d-flex flex-column">
                      <div className="d-flex justify-content-between">
                        <div className="flex-fill w-100">{option.label}</div>
                        <div className="overflow-hidden text-truncate">
                          {option.financiers.length > 0 && (
                            <div className="text-secondary ms-2 me-n3">
                              {option.financiers.map((item) => (
                                <small
                                  className="me-2"
                                  key={`${option.id}${item.id}${item.identification}`}
                                >
                                  {[t(item.id, item.label), item.identification]
                                    .filter(Boolean)
                                    .join(" #")}
                                </small>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {items.length > 0 && (
                        <div className="text-secondary mt-n1 text-truncate overflow-hidden">
                          {items.filter(Boolean).map((item) => (
                            <small className="me-2" key={`${option.id}${item}`}>
                              {item}
                            </small>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }}
                renderInput={renderInput({
                  isInvalid: !event.consumer?.id,
                })}
              />
              {event.consumer.id && (
                <div className="form-control-actions d-flex align-items-center">
                  <button
                    className="btn btn-sm p-0 btn-link"
                    type="button"
                    data-consumer={JSON.stringify({
                      id: event.consumer.id,
                    })}
                    data-bs-toggle="modal"
                    data-bs-target="#consumersModal"
                  >
                    {t("edit", { context: "consumer" })}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <div className="d-grid gap-2 mb-2">
          {/* Bug real (Trello "V2 | Arreglos de Responsive Design",
              2026-09-04): event.resource/service/financier/location sin `?.`
              acá — a diferencia del `highlight` de cada componente, un renglón
              más abajo, que YA los accedía con `?.`. Un turno real puede
              carecer de cualquiera de estos campos (mismo hallazgo ya
              confirmado en vivo contra QA para Agenda/Event/Event.js y
              Simple.js); disparado acá por el test de "Horarios disponibles"
              con resource/location undefined. */}
          <Resources
            onChange={value => onChange({ target: { name: "resource", value: value || {} } })}
            store="manual"
            value={event.resource?.id ? { id: event.resource.id, label: event.resource.label } : null}
            facets={facets.resources || {}}
            highlight={!event.resource?.id}

          />
          {event.type !== "lock" && (
            <>
              <Services
                isLoading={availability.isFetching}
                onChange={value => onChange({ target: { name: "service", value: value || {} } })}
                store="manual"
                value={event.service?.key ? { key: event.service.key, label: event.service.label } : null}
                facets={facets.services || {}}
                highlight={!event.service?.key}
              />
              <Financiers
                isLoading={availability.isFetching}
                onChange={value => onChange({ target: { name: "financier", value: value || {} } })}
                store="manual"
                value={event.financier?.key ? { key: event.financier.key, label: t(event.financier.key, event.financier.label) } : null}
                facets={availability.financiers.facets}
                top={availability.financiers.consumer}
              />
              <Locations
                isLoading={availability.isFetching}
                onChange={value => onChange({ target: { name: "location", value: value || {} } })}
                store="manual"
                value={event.location?.id ? { id: event.location.id, label: event.location.label } : null}
                facets={facets.locations || {}}
                highlight={!event.location?.id}
              />
              {event.id && event.token && event.remote && (
                <div className="text-end">
                  {/* Hallazgo 2026-07-27 (reporte QA Juan Cruz): este link SIEMPRE
                      abría en pestaña nueva y nunca mandaba el JWT del profesional
                      ni el rol — a diferencia del botón "Videoconsulta" de la
                      Agenda (Agenda/Event/Event.js), que abre la MISMA sala
                      embebida (useStoreRooms/Room.js) con las credenciales reales.
                      Reusar ese mecanismo acá da la experiencia completa (agregar
                      indicación/adjuntar archivo) también desde el Calendario,
                      sin duplicar lógica de rol/token (Room.js ya la resuelve). */}
                  <button
                    type="button"
                    className="btn btn-link p-0 small link-info my-1"
                    onClick={() => {
                      openRoom(`${event.id}/${event.token}`);
                      // Pedido de reunión 2026-07-29 (Juan Cruz): además de
                      // abrir el modal de la llamada, navegar la página a la
                      // HCE del paciente — la llamada queda flotando encima
                      // mientras se ve la HCE debajo. Solo si tiene permiso
                      // de EHR (mismo gate que el botón "Ir a HCE" de Room.js).
                      if (hasEhr && event.consumer?.id) {
                        navigate(
                          window._l(`/${event.consumer.id}/ehr?eventID=${event.id.split("/")[1]}`)
                        );
                      }
                    }}
                  >
                    {t("open_telemedicine_link", 'Abrir videoconsulta')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

          <>
            {event.type === 'lock' && (
              <label className="form-label">{t('start_end_date_and_time', 'Fechas de inicio y fin')}</label>
            )}
            {/* Fix responsive (2026-08-14): fila sin flex-wrap — en un
                celular angosto (320-360px) el input de fecha (flex-fill) y
                el de hora (150px) no entraban en una línea y no había forma
                de que colapsen. El `!important` del width del input de hora
                además es inválido dentro de un atributo style inline (el
                navegador lo descarta entero), así que ni siquiera cumplía
                su propio objetivo de acotar el ancho. */}
            {/* Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA):
                el fix de arriba agregó flex-wrap pero dejó el input de fecha
                con la clase `flex-fill` de Bootstrap (`flex: 1 1 auto
                !important`) — con flex-basis:auto, el algoritmo de wrap de
                flexbox decide si corta de línea usando el ancho de
                contenido intrínseco del input (bastante mayor a 140px), no
                el minWidth. Eso forzaba el wrap a 2 filas en el offcanvas de
                turno (460px) aun sobrando espacio de sobra para las dos
                filas. Se reemplaza flex-fill por un flex-basis explícito de
                140px para que la decisión de wrap sea la real (140+150+gap
                cabe en el offcanvas) y siga colapsando en mobile angosto. */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <input
                name="day"
                id="day"
                minLength="6"
                maxLength="8"
                type="date"
                pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
                placeholder={t("day_placeholder", {
                  context: "events",
                })}
                className={cx("form-control", {
                  "is-invalid": props.day !== false && (!event.day || ((!event.id && event.day !== props.slot?.day) && ((!Object.values(slots?.[event?.day] || {}).some(value => value.capacity)) && event.type !== 'lock'))),
                })}
                style={{ flex: "1 1 140px", minWidth: 140 }}
                value={event.day || ""}
                onChange={onChange}
              />

              <input
                step="300"
                pattern="[0-9]{2}:[0-9]{2}"
                required
                type="time"
                style={{ width: 150 }}
                name="time"
                id={`time`}
                value={event.time || ""}
                onChange={onChange}
                className={cx("form-control", {
                  "is-invalid": props.time !== false && (!event.time || ((!event.id && event.day !== props.slot?.day && event.time !== props.slot?.time) && (!slots?.[event?.day]?.[event?.time]?.capacity && event.type !== 'lock'))),
                })}
                placeholder={t("time_placeholder", {
                  context: "events",
                })}
              />
            </div>

            {event.type === "lock" && (
              <div className="mb-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <input
                    minLength="6"
                    maxLength="8"
                    type="date"
                    pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
                    name="untilDay"
                    id="eventUntilDay"
                    value={event.untilDay || ""}
                    onChange={onChange}
                    className={cx("form-control", {
                      "is-invalid": !event.untilDay,
                    })}
                    style={{ flex: "1 1 140px", minWidth: 140 }}
                  />
                  <input
                    step="300"
                    pattern="[0-9]{2}:[0-9]{2}"
                    required
                    type="time"
                    style={{ width: 150 }}
                    name="untilTime"
                    id="eventUntilTime"
                    value={event.untilTime || ""}
                    onChange={onChange}
                    className={cx("form-control", {
                      "is-invalid": !event.untilTime,
                    })}
                  />
                </div>
              </div>
            )}

            {/* Estado del turno — visible directamente en el form (solo appointments) */}
            {event.type !== "lock" && (
              <Select
                floating={false}
                required
                id="eventStatus"
                name="status"
                value={event.status || "booked"}
                onChange={onChange}
                placeholder={t("status", { context: "events" })}
                options={[
                  "booked",
                  "arrived",
                  "active",
                  "fulfilled",
                  "noshow",
                  "cancelled",
                  "pending",
                ].map((value) => ({
                  value,
                  label: t(`status_${value}`, { context: "events" }),
                }))}
              />
            )}
          </>
      </div>

      {/* Segmented control TURNO ÚNICO / SESIONES + config de recurrencia */}
      {!event.id && event.type !== 'lock' && (
        <div className={styles.sessionDetails}>
          <div className={styles.segmentedControl}>
            <button
              type="button"
              className={cx(styles.segment, { [styles.segmentActive]: batchMode === 'single' })}
              onClick={() => switchBatchMode('single')}
            >
              {t('single_appointment', 'TURNO ÚNICO')}
            </button>
            <button
              type="button"
              className={cx(styles.segment, { [styles.segmentActive]: batchMode === 'sessions' })}
              onClick={sessionsLock.guard(() => switchBatchMode('sessions'))}
            >
              <LockedLabel blocked={sessionsLock.blocked}>{t('sessions', 'SESIONES')}</LockedLabel>
            </button>
          </div>

          {batchMode === 'sessions' && (
            <div className={styles.recurrenceConfig}>
              <div className={styles.sessionCountRow}>
                <div className={styles.sessionCountLabel}>
                  <span className={styles.sessionCountTitle}>{t('sessions_count', 'Cantidad de sesiones')}</span>
                  <span className={styles.sessionCountMax}>{t('max_sessions', 'Máx. 20')}</span>
                </div>
                <div className={styles.stepper}>
                  <button
                    type="button"
                    className={styles.stepperBtn}
                    onClick={() => setRecurrence(r => ({ ...r, count: Math.max(2, r.count - 1) }))}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>remove</span>
                  </button>
                  <span className={styles.stepperValue}>{recurrence.count}</span>
                  <button
                    type="button"
                    className={styles.stepperBtn}
                    onClick={() => setRecurrence(r => ({ ...r, count: Math.min(20, r.count + 1) }))}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
                  </button>
                </div>
              </div>
              <div className={styles.freqSection}>
                <span className={styles.freqLabel}>{t('sessions_frequency', 'Frecuencia')}</span>
                <div className={styles.freqBtns}>
                  {UI_FREQUENCY_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      type="button"
                      className={cx(styles.freqPill, { [styles.freqPillActive]: recurrence.frequency === o.value })}
                      onClick={() => setRecurrence(r => ({ ...r, frequency: o.value }))}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.sessionInfoBox}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#13629b', flexShrink: 0, marginTop: 1 }}>event_repeat</span>
                {recurrence.count > 0 && recurrence.frequency ? (
                  <div>
                    <p className={styles.sessionSummaryTitle}>
                      {recurrence.count} {t('sessions_label', 'sesiones')} · {UI_FREQUENCY_OPTIONS.find(o => o.value === recurrence.frequency)?.label}
                    </p>
                    {event.day && (
                      <p className={styles.sessionSummaryRange}>
                        {fmtShortDate(new Date(`${event.day}T12:00`))} → {calcEndDate(event.day, recurrence.count, recurrence.frequency)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className={styles.sessionInfoText}>
                    {t('sessions_helper', 'Definí la cantidad de sesiones y la frecuencia para generar los turnos.')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Acciones de grupo — solo en evento existente que pertenece a un grupo */}
      {event.id && event.groupID && event.type !== 'lock' && (
        <div className={styles.groupActions}>
          <div className={styles.groupInfoRow}>
            {groupEventsQuery.isLoading ? (
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">{t("loading")}</span>
              </div>
            ) : (
              <>
                <div className={styles.groupInfoLeft}>
                  <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#13629b' }}>event_repeat</span>
                  <div className={styles.groupInfoTextBlock}>
                    <p className={styles.groupInfoTitle}>
                      {groupEventsQuery.data?.length ?? '?'} {t('sessions_label', 'sesiones')}{freqLabel ? ` · ${freqLabel}` : ''}
                    </p>
                    {groupStartDate && (
                      <p className={styles.groupInfoRange}>
                        {groupStartDate}{groupEndDate ? ` → ${groupEndDate}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.groupEditBtn}
                  onClick={() => setShowGroupModal(true)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <GroupEventsModal
        show={showGroupModal}
        events={groupEventsQuery.data ?? []}
        currentEventId={event.id}
        loading={groupCancelLoading || mutation.isLoading}
        onClose={() => setShowGroupModal(false)}
        onCancelEvent={handleCancelSingleEvent}
        onCancelFuture={() => handleCancelGroup(false)}
        onFetchSlots={fetchAvailability}
        onRescheduleEvent={handleRescheduleFromModal}
      />

      <CancelGroupEventModal
        show={showCancelGroupModal}
        event={event}
        groupEvents={groupEventsQuery.data ?? []}
        loading={groupCancelLoading || mutation.isLoading}
        onClose={() => setShowCancelGroupModal(false)}
        onCancelThis={handleCancelThisFromGroupModal}
        onCancelFuture={handleCancelFutureFromGroupModal}
        onViewSession={handleViewSessionFromCancelModal}
      />

      {/* Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04):
          "Horarios disponibles" se había retirado por pedido de Juan Cruz QA
          (2026-08-25, ver commit 55ca0ff0) asumiendo que estaba deprecado en
          el diseño productivo — según el reporte nuevo esto fue un error y
          el botón debe seguir operativo para turno NUEVO. Se restaura la
          condición/label originales de antes de 55ca0ff0 (otra sesión
          llegó a la misma conclusión por su cuenta, vía un revert directo
          de 55ca0ff0 — mismo resultado, se resuelve el merge quedándose con
          esta versión porque además blinda con `?.`). event.resource/
          event.location pasan a accederse con `?.` (no estaba en el
          original de antes de 55ca0ff0): en Agenda/Event/Event.js y
          Simple.js se confirmó en vivo contra QA que un turno real puede
          carecer de esos campos — mismo criterio defensivo aplicado acá
          para no crashear el formulario si pasa lo mismo en este flujo. */}
      {event.type !== 'lock' && (event.id || (props.reschedule && event.resource?.id && event.location?.id)) && (
        <div className={styles.rescheduleSection}>
          <button className={styles.rescheduleSectionHeader} type="button" onClick={() => setSlotsOpen(o => !o)}>
            <span className={styles.rescheduleSectionLabel}>
              {event.id ? t('reschedule', 'Reprogramar') : t('times_slots', 'Horarios disponibles')}
            </span>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
              {slotsOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </span>
          </button>
          <div className={cx("border-top p-2", { "d-none": !slotsOpen })}>
            <div className="d-flex flex-column gap-3">
              {Object.keys(slots).sort().map(day => (
                <div key={day}>
                  <div className={cx("small text-secondary text-first-up mb-1 lh-1", { "fw-bold": day === event.day })}>
                    {formatDate(day, "eeee dd - MMMM yyyy")}
                  </div>
                  <div className="d-flex flex-nowrap gap-2 overflow-auto">
                    {Object.entries(slots[day]).filter(([time, entry]) => entry.capacity).sort().map(([time, entry]) => (
                      <button
                        key={`${day}${time}`}
                        type="button"
                        onClick={() => {
                          setEvent({ ...event, day, time });
                          setSlotsOpen(false);
                        }}
                        className={cx("btn btn-sm", {
                          "btn-primary": day === event.day && time === event.time,
                          "btn-outline-primary": day !== event.day || time !== event.time,
                        })}
                      >{time}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {event.id && (
              <button
                type="button"
                disabled={mutation.isLoading}
                className={cx("btn btn-sm btn-warning text-dark mt-3")}
                onClick={() => {
                  store.update({
                    event, reschedule,
                    resource: event.resource,
                    location: event.location,
                    service: event.service,
                    date: event.day,
                  })
                  if (location.pathname.includes('/events/agenda/')) {
                    navigate(location.pathname.replace('/agenda/', '/calendar/'), { replace: true })
                  }
                  onClose()
                }}
              >
                {t("reschedule_from_calendar", "Reprogramar desde el calendario")}
              </button>
            )}
          </div>
        </div>
      )}

      <div className={styles.optionsSection}>
        <button className={styles.optionsHeader} type="button" data-bs-toggle="collapse" data-bs-target="#collapseOptions" aria-expanded="false" aria-controls="collapseOptions">
          <span className={styles.optionsHeaderLeft}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>settings</span>
            {t('advanced_config', 'OPCIONES')}
          </span>
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>keyboard_arrow_down</span>
        </button>

        <div className="collapse" id="collapseOptions">
          <div className={styles.optionsContent}>
            {event.type !== "lock" && (
              <div
                className={styles.optionRow}
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t("overbooking_tip", { context: "events" })}
              >
                <span className={styles.optionLabel}>{t("overbooking", { context: "events" })}</span>
                <ToggleSwitch checked={event.overbooking || false} name="overbooking" onChange={onChange} />
              </div>
            )}

            {event.type === "lock" ? (
              <div
                className={styles.optionRow}
                data-tooltip-id="lock-data-tip"
                data-tooltip-content={t("event_cancellable_tip")}
                data-tooltip-place="bottom"
              >
                <span className={styles.optionLabel}>{t("cancellable")}</span>
                <ToggleSwitch checked={!!event.cancellable} name="cancellable" onChange={onChange} />
              </div>
            ) : (
              <div
                className={styles.optionRow}
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t("remote_tip", { context: "events" })}
              >
                <span className={styles.optionLabel}>
                  <LockedLabel blocked={telemedicineOnBlocked}>{t("remote", { context: "events" })}</LockedLabel>
                </span>
                <ToggleSwitch
                  checked={event.remote || false}
                  name="remote"
                  onChange={telemedicineOnBlocked ? telemedicineLock.open : onChange}
                />
              </div>
            )}

            {event.type !== "lock" && (
              <div
                className={styles.optionRow}
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t("notify_tip", { context: "events" })}
              >
                <span className={styles.optionLabel}>{t("notify", { context: "events" })}</span>
                <ToggleSwitch checked={event.notify ?? true} name="notify" onChange={onChange} />
              </div>
            )}
            {(event.type === "lock" || (event.notify ?? true)) && (
              <Text
                floating={false}
                name="instructions"
                value={event.instructions || ""}
                onChange={onChange}
                placeholder={t("consumer_message", { context: "events" })}
                rows={1}
              />
            )}

            {event.type !== "lock" && (
              <Text
                floating={false}
                name="comments"
                value={event.comments || ""}
                onChange={onChange}
                placeholder={t("internal_note", { context: "events" })}
                rows={1}
              />
            )}

            {event.type === "lock" && props.lockForm === "day" && (
              <Tooltip id="lock-data-tip" />
            )}
          </div>
        </div>
      </div>

      {sessionsLock.modal}
      {telemedicineLock.modal}
      <BatchSessionsModal
        show={showBatchModal}
        slots={batchSlots}
        consumer={consumer}
        loading={batchLoading}
        onClose={() => setShowBatchModal(false)}
        onConfirm={handleBatchCreate}
        onSlotChange={handleSlotChange}
        fetchAvailability={fetchAvailability}
      />

      {event.id && event.source === "crontu" && (
        <div className="mb-2">
          <SendToPatient event={event} />
        </div>
      )}

      <div className={styles.footerBar}>
        <button type="button" className={styles.btnCancel} onClick={onClose}>
          {t("cancel_action", "Cancelar")}
        </button>

        {loading ? (
          <span className="spinner-border spinner-border-sm align-self-center" role="status" aria-hidden="true" />
        ) : !event.id && batchMode === 'sessions' ? (
          <button
            type="button"
            disabled={batchLoading || !isValid || recurrence.count < 2 || !recurrence.frequency}
            className={styles.btnPrimary}
            onClick={handleBatchCheck}
          >
            {batchLoading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            {t("create_sessions", "CREAR SESIONES")}
          </button>
        ) : !isEqual(query.data, event) && !/^events\/lock-/.test(event.id) ? (
          <button
            type="button"
            disabled={mutation.isLoading || !isValid}
            className={styles.btnPrimary}
            onClick={save}
          >
            {mutation.isLoading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
            {t("save", { context: "events" })}
          </button>
        ) : event.id ? (
          <button
            type="button"
            disabled={mutation.isLoading}
            className={event.status !== "cancelled" ? styles.btnDanger : styles.btnSuccess}
            onClick={toggle}
          >
            {event.status !== "cancelled"
              ? t("cancel", { context: ["events", event.type || "appointment"].filter(Boolean).join("_") })
              : t("reschedule", { context: ["events", event.type || "appointment"].filter(Boolean).join("_") })
            }
          </button>
        ) : (
          <button
            type="button"
            disabled={!isValid}
            className={styles.btnPrimary}
            onClick={save}
          >
            {t("save", { context: "events" })}
          </button>
        )}
      </div>
    </div>
  );
};

export default Form;
